import React, { useState, useMemo } from 'react';
import cn from 'classnames';
import { useDebounce } from 'use-debounce/lib';
import moment from 'moment';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Input from '../Input';
import Checkbox from '../Checkbox';
import Shimmer from '../Shimmer';
import {
  ShareSettingsForm,
  ShareSettings,
  defaultShareSettings,
  shareSettingsFromTemplate,
  buildDownloadOptions,
  buildPreviewOptions,
  buildBranding,
  daysToExpiryValue,
  EXPIRY_OPTIONS,
  PURPOSE_PRESETS,
  PurposePreset,
} from '../ShareSettingsForm';

import { useSharesListQuery } from 'src/api/queries/shareQueries';
import { useTemplatesListQuery } from 'src/api/queries/templateQueries';
import { useCreateShareMutation, useAddAssetsToShareMutation } from 'src/api/mutations/shareMutations';
import {
  Share,
  ShareTemplate,
  CreateShareRequest,
  getShareExpiry,
  getShareTitle,
  getShareAssetCount,
} from 'src/types/TShare';
import { config } from 'src/utils/config';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { Copy, Checks, CaretDown, CaretRight, CaretLeft } from 'src/common/icons';

import styles from './AddAssetsToShare.module.scss';

type AddAssetsToShareProps = BaseModalProps & {
  assetIds: string[];
  onSuccess?: () => void;
  /** Skip the purpose grid and open straight on this preset's configure step.
   *  Set by the Send-for-review wizard, which has already established that the
   *  purpose is a review — re-asking would put the mechanism back in front of
   *  the user, which is the thing that wizard exists to remove. */
  lockedPresetId?: string;
};

type Step = 'purpose' | 'configure' | 'existing';

export const AddAssetsToShare: React.FC<AddAssetsToShareProps> = ({
  assetIds,
  onSuccess,
  lockedPresetId,
  ...modalProps
}) => {
  const lockedPurpose = React.useMemo(
    () => (lockedPresetId ? PURPOSE_PRESETS.find((p) => p.presetId === lockedPresetId) : undefined),
    [lockedPresetId],
  );
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const printExportAvailable = usePrintExportAvailable();

  const [step, setStep] = useState<Step>(lockedPurpose ? 'configure' : 'purpose');

  // Provenance of the share being created
  const [selectedPurpose, setSelectedPurpose] = useState<PurposePreset | null>(lockedPurpose ?? null);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(lockedPurpose?.presetId ?? '');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Share-level fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sharedWith, setSharedWith] = useState('');
  const [expiry, setExpiry] = useState(lockedPurpose?.expiry ?? 'WEEK');
  const [dueOn, setDueOn] = useState('');
  const [enablePassword, setEnablePassword] = useState(false);
  const [password, setPassword] = useState('');

  // Shared branding / preview / download settings
  const [settings, setSettings] = useState<ShareSettings>(lockedPurpose?.settings ?? defaultShareSettings());
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Add to existing share state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermDebounced] = useDebounce(searchTerm, 250);
  const [selectedShareId, setSelectedShareId] = useState<string | null>(null);

  // Success state
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdShareUrl, setCreatedShareUrl] = useState('');

  // Queries
  const { data: sharesResult, isLoading: isLoadingShares } = useSharesListQuery(
    { limit: 100, offset: 0 },
    undefined,
    { enabled: step === 'existing' }
  );

  const { data: templatesResult } = useTemplatesListQuery(undefined, {
    enabled: step === 'purpose',
  });
  const templates = templatesResult?.data?.templates || [];

  const isPurposeDisabled = (purpose: PurposePreset): boolean =>
    purpose.requiresAddon === 'PRINT_EXPORT' && !printExportAvailable;

  const handleSelectPurpose = (purpose: PurposePreset) => {
    if (isPurposeDisabled(purpose)) return;
    setSelectedPurpose(purpose);
    setSelectedPresetId(purpose.presetId);
    setSelectedTemplateId('');
    setSettings(purpose.settings);
    setExpiry(purpose.expiry);
    setAdvancedOpen(false);
    setStep('configure');
  };

  const handleSelectTemplate = (templateId: string) => {
    const template = templates.find((t: ShareTemplate) => t.template_id === templateId);
    if (!template) return;
    setSelectedTemplateId(templateId);
    setSelectedPresetId('');
    setSelectedPurpose(null);
    setSettings(shareSettingsFromTemplate(template));
    if (template.default_expiry_days) {
      setExpiry(daysToExpiryValue(template.default_expiry_days));
    }
    setAdvancedOpen(false);
    setStep('configure');
  };

  // Mutations
  const createShareMutation = useCreateShareMutation({
    onSuccess: (data) => {
      const shareUrl = data.url || `${config.hostUrl}/shared/${data.id}`;
      setCreatedShareUrl(shareUrl);
      setIsSuccess(true);
      showSuccess('Share created successfully');
      onSuccess?.();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const addToShareMutation = useAddAssetsToShareMutation(selectedShareId || '', {
    onSuccess: () => {
      showSuccess('Assets added to share successfully');
      closeModal();
      onSuccess?.();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const filteredShares = useMemo(() => {
    const shares = sharesResult?.data?.shares || [];
    if (!searchTermDebounced) return shares;
    return shares.filter((share: Share) =>
      getShareTitle(share).toLowerCase().includes(searchTermDebounced.toLowerCase())
    );
  }, [sharesResult, searchTermDebounced]);

  const handleCreateShare = () => {
    if (!title.trim()) {
      showAlert('Please enter a title for the share');
      return;
    }

    const emailList = sharedWith
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const shareData: CreateShareRequest = {
      file_ids: assetIds,
      title: title.trim(),
      description: description.trim() || undefined,
      allow_feedback: settings.allowFeedback,
      allow_approval: settings.allowApproval,
      branding: buildBranding(settings),
      download_options: buildDownloadOptions(settings),
      preview_options: buildPreviewOptions(settings),
      access_control: {
        expiry,
        password: enablePassword ? password : null,
      },
      preset_id: selectedPresetId || undefined,
      template_id: selectedTemplateId || undefined,
      email_to: emailList.length > 0 ? emailList : undefined,
      due_on: dueOn || undefined,
    };

    createShareMutation.mutate(shareData);
  };

  const handleAddToExisting = () => {
    if (!selectedShareId) {
      showAlert('Please select a share');
      return;
    }
    addToShareMutation.mutate(assetIds);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(createdShareUrl);
      showSuccess('Link copied to clipboard');
    } catch (err) {
      showAlert('Failed to copy link');
    }
  };

  const getStatusClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OK':
      case 'ACTIVE':
        return styles.active;
      case 'EXPIRED':
        return styles.expired;
      default:
        return '';
    }
  };

  const formatExpiryDate = (share: Share): string => {
    const exp = getShareExpiry(share);
    if (!exp) return 'Never';
    return moment.unix(exp).format('MMM D, YYYY');
  };

  const getDisplayStatus = (status: string): string => {
    if (status?.toUpperCase() === 'OK') return 'Active';
    return status || '-';
  };

  // Success state UI
  if (isSuccess) {
    return (
      <BaseModal {...modalProps}>
        <div className={styles.successState}>
          <div className={styles.successIcon}>
            <Checks />
          </div>
          <h3 className={styles.successTitle}>
            {lockedPurpose ? 'Review sent' : 'Share Created!'}
          </h3>
          <p className={styles.successDescription}>
            {lockedPurpose
              ? 'Reviewers can open the link below. It appears under Reviews until it comes back.'
              : 'Your share has been created. Copy the link below to share with others.'}
          </p>
          <div className={styles.shareUrlContainer}>
            <span className={styles.shareUrl}>{createdShareUrl}</span>
            <button className={styles.copyButton} onClick={handleCopyUrl} title="Copy link">
              <Copy />
            </button>
          </div>
          <Button mode="primary" onClick={closeModal}>
            Done
          </Button>
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal {...modalProps}>
      <h3 className={styles.modalTitle}>
        {lockedPurpose ? 'External review' : 'Share Assets'}
      </h3>
      <p className={styles.modalDescription}>
        {lockedPurpose
          ? `${assetIds.length} asset${assetIds.length !== 1 ? 's' : ''} · reviewers approve or reject each one, on watermarked previews`
          : `Share ${assetIds.length} asset${assetIds.length !== 1 ? 's' : ''} with others`}
      </p>

      {/* Step 1: pick a purpose */}
      {step === 'purpose' && (
        <>
          <div className={styles.purposeGrid}>
            {PURPOSE_PRESETS.map((purpose) => {
              const disabled = isPurposeDisabled(purpose);
              return (
                <button
                  key={purpose.presetId}
                  type="button"
                  className={cn(styles.purposeCard, { [styles.purposeCardDisabled]: disabled })}
                  onClick={() => handleSelectPurpose(purpose)}
                  disabled={disabled}
                >
                  <span className={styles.purposeName}>{purpose.name}</span>
                  <span className={styles.purposeDescription}>{purpose.description}</span>
                  {disabled && (
                    <span className={styles.purposeAddonHint}>
                      Add the Print Export addon to use this
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {templates.length > 0 && (
            <div className={styles.formSection}>
              <label className={styles.formLabel}>Or start from a saved template</label>
              <select
                className={styles.templateSelect}
                value={selectedTemplateId}
                onChange={(e) => handleSelectTemplate(e.target.value)}
              >
                <option value="">Select a saved template…</option>
                {templates.map((template: ShareTemplate) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.name} {template.is_default ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.actions}>
            <Button mode="tertiary" size="small" onClick={() => setStep('existing')}>
              Add to existing share
            </Button>
            <Button mode="tertiary" size="small" onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {/* Step 2: configure the new share */}
      {step === 'configure' && (
        <>
          {!lockedPurpose && (
            <button className={styles.backLink} onClick={() => setStep('purpose')}>
              <CaretLeft />
              <span>
                Back{selectedPurpose ? ` · ${selectedPurpose.name}` : ''}
              </span>
            </button>
          )}

          <div className={styles.formScrollArea}>
            <div className={styles.formSection}>
              <Input
                label="Title *"
                placeholder="Enter share title"
                value={title}
                onChange={setTitle}
              />
            </div>

            <div className={styles.formSection}>
              <Input
                label="Description"
                placeholder="Enter description (optional)"
                value={description}
                onChange={setDescription}
              />
            </div>

            <div className={styles.formSection}>
              <Input
                label="Share With"
                placeholder="Enter email addresses (comma-separated)"
                value={sharedWith}
                onChange={setSharedWith}
              />
              <p className={styles.inputHint}>
                Recipients will receive an email notification with the share link
              </p>
            </div>

            <div className={styles.formSection}>
              <label className={styles.formLabel}>Link expires</label>
              <select
                className={styles.expirySelect}
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className={styles.inputHint}>After this the link stops working.</p>
            </div>

            {/* Only a review has something to be late for. A distribution
                link has an expiry and nothing else. The two live side by side
                because they are so easily conflated: expiry kills the link,
                a due date is advisory and routinely missed. */}
            {settings.allowApproval && (
              <div className={styles.formSection}>
                <label className={styles.formLabel} htmlFor="share-due-on">
                  Due (optional)
                </label>
                <input
                  id="share-due-on"
                  type="date"
                  className={styles.expirySelect}
                  value={dueOn}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDueOn(e.target.value)}
                />
                <p className={styles.inputHint}>
                  {dueOn
                    ? 'When you need a verdict back. Counts as end of that day; the link stays live either way.'
                    : 'No deadline — this review will never read as overdue.'}
                </p>
              </div>
            )}

            <div className={styles.formSection}>
              <div className={styles.compactToggleRow}>
                <label className={styles.compactToggleItem}>
                  <Checkbox
                    checked={enablePassword}
                    onChange={(e) => setEnablePassword(e.target.checked)}
                  />
                  <span>Password Protection</span>
                </label>
              </div>
              {enablePassword && (
                <div className={styles.passwordSection}>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={setPassword}
                  />
                </div>
              )}
            </div>

            {settings.printReadyDownload && (
              <div className={styles.printReadyNote}>
                Print-ready downloads serve assets that already have a print export.
                After creating the share, open it to see availability and generate
                print files for the remaining assets.
              </div>
            )}

            {/* Advanced disclosure: full branding / preview / download controls */}
            <button
              type="button"
              className={styles.advancedToggle}
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((o) => !o)}
            >
              {advancedOpen ? <CaretDown /> : <CaretRight />}
              <span>Customize advanced options</span>
            </button>

            {advancedOpen && (
              <ShareSettingsForm
                value={settings}
                onChange={setSettings}
                showPrintReady={printExportAvailable}
              />
            )}
          </div>

          <div className={styles.actions}>
            <Button
              mode="tertiary"
              size="small"
              onClick={() => (lockedPurpose ? closeModal() : setStep('purpose'))}
            >
              {lockedPurpose ? 'Cancel' : 'Back'}
            </Button>
            <Button
              mode="primary"
              size="small"
              onClick={handleCreateShare}
              loading={createShareMutation.isLoading}
              disabled={!title.trim() || createShareMutation.isLoading}
            >
              {lockedPurpose ? 'Send review link' : 'Create Share'}
            </Button>
          </div>
        </>
      )}

      {/* Add to existing share */}
      {step === 'existing' && (
        <>
          <button className={styles.backLink} onClick={() => setStep('purpose')}>
            <CaretLeft />
            <span>Back</span>
          </button>

          <Input
            className={styles.search}
            placeholder="Search shares..."
            variant="secondary"
            value={searchTerm}
            onChange={setSearchTerm}
          />

          <div className={styles.sharesList}>
            {isLoadingShares ? (
              <div className={styles.loadingShimmer}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Shimmer key={i} w="100%" h="4rem" radius="0.25rem" />
                ))}
              </div>
            ) : filteredShares.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No shares found. Create a new share to get started.</p>
              </div>
            ) : (
              filteredShares.map((share: Share) => (
                <div
                  key={share.id}
                  className={cn(styles.shareItem, {
                    [styles.selected]: selectedShareId === share.id,
                  })}
                  onClick={() => setSelectedShareId(share.id)}
                >
                  <Checkbox
                    radio
                    checked={selectedShareId === share.id}
                    onChange={() => setSelectedShareId(share.id)}
                  />
                  <div className={styles.shareItemInfo}>
                    <span className={styles.shareItemTitle}>{getShareTitle(share)}</span>
                    <div className={styles.shareItemMeta}>
                      <span>{getShareAssetCount(share)} assets</span>
                      <span>Expires {formatExpiryDate(share)}</span>
                    </div>
                  </div>
                  <span className={cn(styles.shareItemStatus, getStatusClass(share.status))}>
                    {getDisplayStatus(share.status)}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className={styles.actions}>
            <Button mode="tertiary" size="small" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              mode="primary"
              size="small"
              onClick={handleAddToExisting}
              loading={addToShareMutation.isLoading}
              disabled={!selectedShareId || addToShareMutation.isLoading}
            >
              Add to Share
            </Button>
          </div>
        </>
      )}
    </BaseModal>
  );
};

export default AddAssetsToShare;
